/*
 * ColorPark case customiser.
 *
 * One state machine owns the whole full-screen root:
 *
 *   idle -> warming -> loading -> adding -> editing -> saving -> complete
 *                       \-> load_error -> retry or return to product
 *                                       \-> abandoned
 *                                       \-> recoverable_error
 *
 * Every visual change goes through setState, so the root can never end up
 * showing a black full-screen shell with a hidden iframe inside it. If you add
 * behaviour here, drive it from data-state rather than toggling elements.
 *
 * The ordering in finish() matters and is not arbitrary. The shopper sees the
 * Shopify saving layer BEFORE the cart write is attempted, and sees success
 * only after the cart has been re-read and verified. A design that reached the
 * printer but not the cart line is an order nobody can fulfil, so a spinner
 * that resolves into a retry is the honest outcome, never a completion screen.
 */
if (!customElements.get('colorpark-customise')) {
  customElements.define(
    'colorpark-customise',
    class ColorparkCustomise extends HTMLElement {
      connectedCallback() {
        this.cfg = {
          goodsId: this.dataset.goodsId,
          machineId: this.dataset.machineId,
          editorUrl: this.dataset.editorUrl,
          returnUrl: this.dataset.returnUrl,
          cartAdd: this.dataset.cartAddUrl,
          cartChange: this.dataset.cartChangeUrl,
          abandon: this.dataset.abandon || 'remove',
          language: this.dataset.language || 'en-us',
          saveTimeout: (parseInt(this.dataset.saveTimeout, 10) || 30) * 1000,
          loadTimeout: 20000,
          // Blank until the app proxy is deployed, in which case the interim
          // theme-asset callback is used instead. Setting it is the switch, and
          // clearing it is the way back if the proxy misbehaves.
          proxyPath: (this.dataset.proxyPath || '').replace(/\/+$/, ''),
        };

        this.button = this.querySelector('[data-cp-button]');
        this.errorEl = this.querySelector('[data-cp-error]');
        this.variantId = this.dataset.variantId;
        this.variantAvailable = this.dataset.variantAvailable === 'true';

        // The root is moved out to <body> so no transformed or clipped product
        // container can trap a position:fixed layer. Once moved it is never
        // recreated, only re-stated, which is what keeps the warm preload warm.
        this.root = document.getElementById(this.dataset.rootId);
        document.body.appendChild(this.root);
        this.frame = this.root.querySelector('[data-cp-frame]');
        this.closeBtn = this.root.querySelector('[data-cp-close]');
        this.retryBtn = this.root.querySelector('[data-cp-retry]');
        this.loadRetryBtn = this.root.querySelector('[data-cp-load-retry]');
        this.loadCloseBtns = [...this.root.querySelectorAll('[data-cp-load-close]')];
        this.state = 'idle';
        this.backgroundLocked = false;
        this.session = null;
        this.pendingWrite = null;
        this.frameDocumentLoaded = false;
        this.disconnected = false;
        this.warmGeneration = (this.warmGeneration || 0) + 1;
        this.launchPending = false;
        this.closePending = false;

        this.onClick = this.onClick.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.onLoadRetry = this.onLoadRetry.bind(this);
        this.onMessage = this.onMessage.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.button.addEventListener('click', this.onClick);
        this.closeBtn.addEventListener('click', this.onClose);
        this.retryBtn?.addEventListener('click', this.onRetry);
        this.loadRetryBtn?.addEventListener('click', this.onLoadRetry);
        this.loadCloseBtns.forEach((button) => button.addEventListener('click', this.onClose));
        window.addEventListener('message', this.onMessage);
        document.addEventListener('keydown', this.onKeydown);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.onVariantChange(event?.data?.variant);
          });
        }

        this.initPromise = this.init();
      }

      /**
       * Pick up a design that a reload interrupted, before warming.
       *
       * Without this, reloading mid design left the pending line in the cart,
       * lost the session, and made the next press of Customise add a SECOND
       * line. The shopper ended up with two undesigned cases and, once checkout
       * validation is live, no way to check out and no obvious way to fix it.
       *
       * Recovery has to happen before warm(), because warm() mints the design
       * token that the server session is opened against. Recovering afterwards
       * would leave the cart line on the old token and the server record on the
       * new one, which quietly breaks order reconciliation.
       */
      async init() {
        this.restorePromise = this.restoreSession();
        await this.restorePromise;
        this.scheduleWarm();
      }

      async restoreSession() {
        this.restoreFailed = false;
        const stored = this.readStoredSession() || this.pendingWrite;
        if (!stored?.token) return;
        if (stored.pendingWrite) {
          this.pendingWrite = stored;
          this.pendingToken = stored.token;
        }

        // The cart is the authority, not the browser. A line the shopper
        // removed by hand, or a cart that expired, must not be resurrected.
        let line = null;
        try {
          const cart = await this.readRecoveryCart();
          line = cart.items.find((i) => i.properties?._colorpark_design_token === stored.token) || null;
        } catch (error) {
          // An unreadable cart may still contain this design. Do not mint a
          // new token or add a duplicate line; the next explicit launch retries.
          this.restoreFailed = true;
          return;
        }

        if (!line && stored.pendingWrite) {
          // The add may still commit after this cart read. An empty cart is
          // not permission to retransmit an uncertain POST with the same token.
          return;
        }
        if (!line || line.properties?._colorpark_status === 'submitted') {
          this.clearSession();
          if (stored.pendingWrite && line) {
            this.resetFrame();
            this.recoveryGeneration = this.warmGeneration;
          }
          return;
        }

        // Key comes from the cart, not from storage, because it changes
        // whenever properties change.
        this.session = { token: stored.token, lineKey: line.key, variantId: String(line.variant_id) };
        this.pendingWrite = null;
        this.pendingToken = stored.token;
        this.persistSession();
        this.syncLabel();
        this.track('session_recovered');
      }

      async readRecoveryCart() {
        const controller = new AbortController();
        let timeout;
        try {
          const stopped = new Promise((resolve, reject) => {
            timeout = window.setTimeout(() => {
              controller.abort();
              reject(new Error('Could not recover the design'));
            }, 8000);
          });
          const request = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
            signal: controller.signal,
          }).then((res) => {
            if (!res.ok) throw new Error('Could not read the cart');
            return res.json();
          });
          return await Promise.race([request, stopped]);
        } finally {
          window.clearTimeout(timeout);
        }
      }

      /**
       * One place decides what the button says, so a recovered design cannot
       * leave "Finish your design" showing after it has been completed or
       * abandoned.
       */
      syncLabel() {
        const label = this.querySelector('[data-cp-label]');
        if (!label) return;
        if (!this.variantAvailable) label.textContent = this.dataset.soldOutLabel;
        else if (this.session?.lineKey) label.textContent = this.dataset.resumeLabel;
        else label.textContent = this.dataset.customiseLabel;
      }

      disconnectedCallback() {
        this.disconnected = true;
        this.invalidateWarm();
        this.button?.removeEventListener('click', this.onClick);
        this.closeBtn?.removeEventListener('click', this.onClose);
        this.retryBtn?.removeEventListener('click', this.onRetry);
        this.loadRetryBtn?.removeEventListener('click', this.onLoadRetry);
        this.loadCloseBtns.forEach((button) => button.removeEventListener('click', this.onClose));
        window.removeEventListener('message', this.onMessage);
        document.removeEventListener('keydown', this.onKeydown);
        this.unsubscribeVariant?.();
        this.clearWatchdog();
        this.lockBackground(false);
        this.root?.remove();
      }

      /* ---------------------------------------------------------------- state */

      setState(next) {
        this.state = next;
        this.root.dataset.state = next;

        const editing = next === 'editing';
        const busy = ['loading', 'adding', 'load_error', 'editing', 'saving', 'complete', 'recoverable_error'].includes(next);

        // Never rely on the iframe merely being hidden: while it is not the
        // active surface it must also be unreachable by pointer and by tab.
        this.frame.setAttribute('aria-hidden', editing ? 'false' : 'true');
        this.frame.tabIndex = editing ? 0 : -1;
        this.root.setAttribute('aria-hidden', busy ? 'false' : 'true');
        this.root.setAttribute('aria-busy', String(next === 'loading' || next === 'adding'));
        this.loadCloseBtns.forEach((button) => { button.disabled = next === 'adding'; });

        this.lockBackground(busy);
        this.track(`state_${next}`);
      }

      lockBackground(locked) {
        if (locked === this.backgroundLocked) return;
        this.backgroundLocked = locked;

        if (locked) {
          this.scrollY = window.scrollY;
          document.body.style.top = `-${this.scrollY}px`;
          document.body.classList.add('colorpark-locked');
          document.querySelectorAll('body > *:not(.colorpark-root)').forEach((el) => {
            if (el.inert !== undefined) el.inert = true;
          });
        } else {
          document.body.classList.remove('colorpark-locked');
          document.body.style.top = '';
          document.querySelectorAll('body > *:not(.colorpark-root)').forEach((el) => {
            if (el.inert !== undefined) el.inert = false;
          });
          if (this.scrollY != null) window.scrollTo(0, this.scrollY);
        }
      }

      /* --------------------------------------------------------------- warming */

      scheduleWarm() {
        if (this.disconnected) return;
        window.clearTimeout(this.rewarmTimer);
        if (this.afterPageLoad) window.removeEventListener('load', this.afterPageLoad);
        const generation = this.warmGeneration;
        this.afterPageLoad = () => {
          this.afterPageLoad = null;
          this.rewarmTimer = window.setTimeout(() => {
            if (!this.disconnected && generation === this.warmGeneration) this.warm();
          }, 300);
        };
        if (document.readyState === 'complete') this.afterPageLoad();
        else window.addEventListener('load', this.afterPageLoad, { once: true });
      }

      waitForPageLoad(signal) {
        if (signal.aborted) return Promise.reject(new Error('cancelled'));
        if (document.readyState === 'complete') return Promise.resolve();
        return new Promise((resolve, reject) => {
          const cleanup = () => {
            window.removeEventListener('load', loaded);
            signal.removeEventListener('abort', cancelled);
          };
          const loaded = () => { cleanup(); resolve(); };
          const cancelled = () => { cleanup(); reject(new Error('cancelled')); };
          window.addEventListener('load', loaded, { once: true });
          signal.addEventListener('abort', cancelled, { once: true });
        });
      }

      loadFrame(src, signal) {
        if (signal.aborted) return Promise.reject(new Error('cancelled'));
        const frame = this.frame;
        return new Promise((resolve, reject) => {
          const cleanup = () => {
            frame.removeEventListener('load', loaded);
            frame.removeEventListener('error', failed);
            signal.removeEventListener('abort', failed);
          };
          const loaded = () => {
            if (frame !== this.frame || frame.getAttribute('src') !== src) return;
            // A freshly replaced frame can still deliver its initial blank
            // document's load event. That is not the remote editor loading.
            try {
              if (frame.contentWindow.location.href === 'about:blank') return;
            } catch (error) { /* Expected for the cross-origin editor document. */ }
            cleanup();
            resolve();
          };
          const failed = () => { cleanup(); reject(new Error('load failed')); };
          frame.addEventListener('load', loaded);
          frame.addEventListener('error', failed, { once: true });
          signal.addEventListener('abort', failed, { once: true });
          frame.src = src;
        });
      }

      discardFrame() {
        const replacement = this.frame.cloneNode(false);
        replacement.removeAttribute('src');
        delete replacement.dataset.loaded;
        this.frame.replaceWith(replacement);
        this.frame = replacement;
        this.frameDocumentLoaded = false;
      }

      async warm({ force = false } = {}) {
        const generation = this.warmGeneration;
        await this.restorePromise;
        if (this.disconnected || generation !== this.warmGeneration) return { status: 'cancelled' };
        if (this.restoreFailed || this.pendingWrite) return { status: 'failed' };
        if (this.warmPromise) return this.warmPromise;
        if (this.frameDocumentLoaded) return { status: 'loaded', token: this.pendingToken };
        if (navigator.connection?.saveData && !force) return { status: 'deferred' };

        const controller = new AbortController();
        this.warmController = controller;
        this.frame.dataset.loaded = 'true';
        if (this.state === 'idle') this.setState('warming');
        this.track('preload_started');
        this.pendingToken = this.pendingToken || this.session?.token || this.randomId();
        const token = this.pendingToken;
        let timedOut = false;
        const timer = window.setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, this.cfg.loadTimeout);
        const work = (async () => {
          try {
            await this.waitForPageLoad(controller.signal);
            const src = await this.editorSrc(token, { signal: controller.signal });
            if (controller.signal.aborted || !src) throw new Error('load failed');
            await this.loadFrame(src, controller.signal);
            if (generation !== this.warmGeneration || this.disconnected) return { status: 'cancelled' };
            this.frameDocumentLoaded = true;
            this.track('preload_document_loaded');
            return { status: 'loaded', token };
          } catch (error) {
            if (generation !== this.warmGeneration || this.disconnected) return { status: 'cancelled' };
            this.discardFrame();
            if (this.state === 'warming') this.setState('idle');
            this.track(timedOut ? 'preload_timeout' : 'preload_failed');
            return { status: 'failed', reason: timedOut ? 'timeout' : 'connection' };
          } finally {
            window.clearTimeout(timer);
            if (this.warmController === controller) this.warmController = null;
          }
        })();
        this.warmPromise = work;
        try {
          return await work;
        } finally {
          if (this.warmPromise === work) this.warmPromise = null;
        }
      }

      invalidateWarm() {
        this.warmGeneration = (this.warmGeneration || 0) + 1;
        this.warmController?.abort();
        this.warmController = null;
        this.warmPromise = null;
        window.clearTimeout(this.rewarmTimer);
        if (this.afterPageLoad) window.removeEventListener('load', this.afterPageLoad);
        this.afterPageLoad = null;
      }

      onLoadRetry() {
        if (this.launchPending || this.state !== 'load_error') return;
        this.track('load_retry_started');
        this.invalidateWarm();
        this.discardFrame();
        return this.openEditor();
      }

      /**
       * Build the editor URL, including where ColorPark sends the shopper next.
       *
       * Whichever callback is in play, the jumpUrl must carry NO query string.
       * ColorPark builds its redirect as `jumpUrl + "?queue_id=" + id` without
       * checking for an existing query, so anything we put there comes back
       * with a second "?" and the queue ID parses as part of our own value.
       * Not theoretical: it stranded a real design on the printer.
       */
      async editorSrc(token, { signal } = {}) {
        const nonce = this.randomId();

        if (this.cfg.proxyPath) {
          // App proxy. The session ID goes in the PATH, because the query has
          // to stay free for ColorPark's append and a fragment would never
          // reach the server that has to look the session up.
          const sessionId = await this.openSession(token, { signal });
          if (signal?.aborted) return null;
          if (sessionId) {
            this.nonce = nonce;
            this.sessionId = sessionId;
            this.callbackOrigin = window.location.origin;
            const proxied = new URL(`${this.cfg.proxyPath}/return/${sessionId}`, window.location.origin);
            return this.editorUrlWith(proxied);
          }
          // Falling through is deliberate: a proxy that is down should degrade
          // to the interim callback rather than take the customiser with it.
          this.track('session_open_failed');
        }

        // Interim theme-asset callback. No server record, and it lands in an
        // opaque origin, so the session values ride in the fragment instead.
        const asset = new URL(this.cfg.returnUrl, window.location.href);
        this.nonce = nonce;
        this.sessionId = null;
        this.callbackOrigin = asset.origin;
        // ColorPark decodes jumpUrl and then truncates it at the first literal
        // ampersand. A fragment such as `nonce=a&parent=b` therefore arrives at
        // the callback with the nonce but without the parent origin, and the
        // callback cannot safely post back to Shopify. Keep both values inside
        // one URL-safe base64 field so there is no delimiter for ColorPark to
        // strip. A page that was already warmed with revision 2 still points at
        // the old cached callback and must be reloaded before submitting.
        asset.hash = `context=${this.callbackContext(this.nonce)}`;
        return this.editorUrlWith(asset);
      }

      editorUrlWith(returnUrl) {
        return `${this.cfg.editorUrl}&lang=${encodeURIComponent(this.cfg.language)}&jumpUrl=${encodeURIComponent(returnUrl.toString())}`;
      }

      async openSession(token, { signal } = {}) {
        const controller = new AbortController();
        let timeout;
        let cancel;
        try {
          // A stalled proxy must fall back to the existing callback, not leave
          // the tutorial's handoff waiting forever. Never retry this POST: an
          // ambiguous response may already have created its session record.
          const stopped = new Promise((resolve) => {
            cancel = () => { controller.abort(); resolve(null); };
            timeout = window.setTimeout(cancel, 8000);
            signal?.addEventListener('abort', cancel, { once: true });
            if (signal?.aborted) cancel();
          });
          const request = fetch(`${this.cfg.proxyPath}/session`, {
            method: 'POST',
            signal: controller.signal,
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({
              design_token: token,
              goods_id: this.cfg.goodsId,
              machine_id: this.cfg.machineId,
              variant_id: this.session?.variantId || this.variantId,
              storefront_origin: window.location.origin,
            }),
          }).then(async (res) => {
            if (!res.ok) return null;
            const body = await res.json();
            return typeof body.session_id === 'string' ? body.session_id : null;
          }).catch(() => null);
          return await Promise.race([request, stopped]);
        } catch (error) {
          return null;
        } finally {
          window.clearTimeout(timeout);
          signal?.removeEventListener('abort', cancel);
        }
      }

      randomId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      callbackContext(nonce) {
        // nonce and origin are ASCII, so btoa is sufficient and avoids pulling
        // a UTF-8 codec into the storefront bundle.
        return window
          .btoa(JSON.stringify({ nonce, parent: window.location.origin }))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      }

      /* ---------------------------------------------------------------- adding */

      async onClick() {
        return this.openEditor();
      }

      async openEditor() {
        if (this.button.getAttribute('aria-disabled') === 'true') return { status: 'failed' };
        if (this.disconnected) return { status: 'cancelled' };
        if (this.launchPending || this.closePending || ['adding', 'editing', 'saving'].includes(this.state)) return { status: 'busy' };

        if (!this.variantAvailable) {
          this.showError(this.dataset.soldOutMessage);
          return { status: 'failed' };
        }

        this.track('customise_clicked');
        this.launchPending = true;
        let generation = this.warmGeneration;
        if (!this.root.contains(document.activeElement)) this.lastFocused = document.activeElement;
        this.setState('loading');
        this.root.querySelector('[data-cp-starting] [data-cp-load-close]')?.focus();
        this.setBusy(true);
        this.clearError();

        try {
          await this.initPromise;
          if (this.restoreFailed || this.pendingWrite) {
            this.restorePromise = this.restoreSession();
            await this.restorePromise;
          }
          // Retiring a proven-submitted token during recovery intentionally
          // resets the frame. This launch can continue on that fresh frame;
          // unrelated resets still cancel the old launch below.
          if (this.recoveryGeneration === this.warmGeneration) generation = this.warmGeneration;
          if (generation !== this.warmGeneration || this.disconnected) return { status: 'cancelled' };
          if (this.pendingWrite) throw new Error(this.dataset.cartPendingMessage || this.dataset.errorMessage);
          const warmed = await this.warm({ force: true });
          if (generation !== this.warmGeneration || this.disconnected || warmed.status === 'cancelled') return { status: 'cancelled' };
          if (warmed.status !== 'loaded' || !this.pendingToken) {
            this.setState('load_error');
            this.loadRetryBtn?.focus();
            return { status: 'failed' };
          }
          // Use the same iframe/token warmed while the tutorial was visible.
          // A recovered line reopens without another cart write.
          if (this.session?.lineKey) {
            this.setBusy(false);
            this.setState('editing');
            this.focusEditor();
            return { status: 'opened' };
          }
          if (!this.variantAvailable) throw new Error(this.dataset.soldOutMessage);
          const token = this.pendingToken;
          const variantId = this.variantId;
          this.setState('adding');
          const result = await this.addCartLine(token, variantId, generation);
          if (result.status !== 'created') {
            throw new Error(result.status === 'rejected' ? result.message : (this.dataset.cartPendingMessage || this.dataset.errorMessage));
          }

          if (this.disconnected || generation !== this.warmGeneration) return { status: 'cancelled' };
          if (this.session?.token !== token || !this.session?.lineKey) throw new Error(this.dataset.cartPendingMessage || this.dataset.errorMessage);

          this.setBusy(false);
          this.setState('editing');
          this.track('editor_opened');
          this.focusEditor();
          return { status: 'opened' };
        } catch (error) {
          if (this.disconnected || generation !== this.warmGeneration) return { status: 'cancelled' };
          this.setBusy(false);
          this.setState('idle');
          this.showError(error.message || this.dataset.errorMessage);
          this.restoreFocus();
          return { status: 'failed' };
        } finally {
          this.launchPending = false;
          if (!this.disconnected) {
            this.setBusy(false);
            if (this.state === 'adding') this.setState('idle');
          }
        }
      }

      async addCartLine(token, variantId, generation) {
        const write = { token, variantId, pendingWrite: true };
        // Record the intent before sending anything. Without durable recovery,
        // a reload after a lost response could add the same case a second time.
        this.pendingWrite = write;
        if (!this.persistSession(write)) {
          this.pendingWrite = null;
          throw new Error(this.dataset.storageUnavailableMessage || this.dataset.errorMessage);
        }

        let timeout;
        const request = (async () => {
          let res;
          try {
            res = await fetch(this.cfg.cartAdd, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: [
                  {
                    id: Number(variantId),
                    quantity: 1,
                    properties: {
                      _colorpark_design_token: token,
                      _colorpark_goods_id: this.cfg.goodsId,
                      _colorpark_machine_id: this.cfg.machineId,
                      _colorpark_status: 'pending',
                    },
                  },
                ],
              }),
            });
            const result = await res.json();
            if (res.status >= 400 && res.status < 500) {
              return { status: 'rejected', message: result?.description || result?.message || this.dataset.errorMessage };
            }
            // A missing key, malformed body or server error may still follow a
            // committed add. Only a definite client rejection permits a retry.
            const lineKey = result?.items?.[0]?.key;
            if (!res.ok || result?.status || typeof lineKey !== 'string' || !lineKey) return { status: 'uncertain' };
            return { status: 'created', lineKey };
          } catch (error) {
            if (res?.status >= 400 && res.status < 500) return { status: 'rejected', message: this.dataset.errorMessage };
            return { status: 'uncertain' };
          }
        })().then((result) => {
          this.settleCartWrite(write, result, generation);
          return result;
        });

        try {
          // Leave the request running: aborting a POST cannot undo a server
          // commit. Late acknowledgement saves recovery data but never opens UI.
          return await Promise.race([request, new Promise((resolve) => {
            timeout = window.setTimeout(() => resolve({ status: 'uncertain' }), 12000);
          })]);
        } finally {
          window.clearTimeout(timeout);
        }
      }

      settleCartWrite(write, result, generation) {
        const stored = this.readStoredSession();
        // A recovered or newer design owns its record. An old response must
        // never overwrite its current cart key or clear its recovery marker.
        if (stored && (stored.token !== write.token || !stored.pendingWrite)) return;
        if (!stored && this.pendingWrite?.token !== write.token) return;

        if (result.status === 'rejected') {
          this.clearSession();
          return;
        }
        if (result.status !== 'created') return;

        const session = { token: write.token, lineKey: result.lineKey, variantId: write.variantId };
        this.persistSession(session);
        if (this.disconnected || generation !== this.warmGeneration) return;
        this.session = session;
        this.pendingWrite = null;
        this.syncLabel();
        this.track('cart_line_created');
        this.publishCartUpdate();
      }

      focusEditor() {
        this.closeBtn.focus();
      }

      /* -------------------------------------------------------------- callback */

      onMessage(event) {
        // Why the origin check here is not what the spec asked for.
        //
        // Shopify serves theme HTML assets under a response header of
        //   content-security-policy: sandbox allow-scripts allow-forms ...
        // with no allow-same-origin. That drops the callback document into an
        // opaque origin, so event.origin arrives as the string "null" and can
        // never be anything else, no matter which domain the asset URL used.
        //
        // So "reject origin null" is not achievable while the callback is a
        // theme asset. It is not a rule that was skipped, it is a rule that
        // this hosting cannot satisfy. An app proxy callback is served as a
        // normal first-party document and does get a real origin, which is why
        // it is required rather than merely tidier.
        //
        // What actually carries the weight in the meantime:
        //   * event.source identity. Only the iframe this element created can
        //     be the source, and that reference cannot be forged by another
        //     page or another frame.
        //   * the per-session nonce, which ties the message to this design.
        //
        // Residual risk to close with the app proxy: the ColorPark document
        // inside our own iframe is also a legitimate event.source and does
        // receive the nonce in its jumpUrl, so it could in principle assert a
        // queue ID we never verified. Only a server that checks the ID against
        // ColorPark can rule that out. Until then the operator matching a paid
        // order to a physical job is the real control.
        //
        // Through the app proxy none of that applies: the callback is a normal
        // first-party document, so demand the exact origin and refuse "null".
        const viaProxy = Boolean(this.sessionId);
        const originOk = viaProxy
          ? event.origin === this.callbackOrigin
          : event.origin === this.callbackOrigin || event.origin === 'null';
        if (!originOk) return;
        if (event.source !== this.frame.contentWindow) return;

        const msg = event.data;
        if (!msg || msg.source !== 'colorpark-complete' || !msg.queueId) return;
        // Proxy mode binds on the session the server issued; interim mode has
        // only the nonce we sent out with the jumpUrl.
        if (viaProxy ? msg.sessionId !== this.sessionId : msg.nonce !== this.nonce) return;
        if (this.state !== 'editing' && this.state !== 'recoverable_error') return;

        this.track('design_submitted');
        this.track('callback_received');

        // The server can say "this completion is wrong" and, until now, we did
        // not listen. `queue-id-conflict` means a SECOND printer job claimed a
        // design that already had one: the server keeps the first ID and flags
        // the clash, and we were reading only `msg.queueId`, calling finish(),
        // and showing the shopper a success screen. The result is a case that
        // gets printed with no order behind it, and nobody finds out.
        //
        // Anything the server flags is an exception, not a completion. Route it
        // to the recoverable path, which is the one that already knows how to
        // ask for a retry rather than claim the design is saved.
        if (msg.problem) {
          this.track('callback_problem_' + String(msg.problem).slice(0, 40));
          this.setState('recoverable_error');
          return;
        }

        this.finish(String(msg.queueId), msg.designImage);
      }

      /**
       * The artwork URL the proxy captured, re-checked here.
       *
       * The proxy already pinned it to img.colorpark.cn, so this is belt and
       * braces. It is worth the six lines: this value goes straight into an
       * <img src> on the cart and onto a paid order, and it arrived by
       * postMessage. Re-validating locally means a change on the other side of
       * that boundary cannot put an arbitrary host in front of our customers.
       *
       * Returns null for anything that is not plainly an https image on that
       * one host. A null simply means no thumbnail.
       */
      safeDesignImage(candidate) {
        if (typeof candidate !== 'string' || !candidate) return null;
        try {
          const u = new URL(candidate);
          if (u.protocol !== 'https:') return null;
          if (u.hostname !== 'img.colorpark.cn') return null;
          if (u.username || u.password) return null;
          u.search = '';
          return u.toString();
        } catch {
          return null;
        }
      }

      async finish(queueId, designImage) {
        this.pendingQueueId = queueId;
        this.pendingDesignImage = this.safeDesignImage(designImage);

        // Order matters: cover the ColorPark surface with our own saving layer
        // first, so the shopper never sees the printer queue page behind it.
        this.setState('saving');
        this.startWatchdog();

        try {
          await this.persistQueueId(queueId);
          const line = await this.verifyCartLine(queueId);
          if (!line) throw new Error('verification failed');

          this.clearWatchdog();
          this.track('queue_persisted');
          this.track('cart_verified');

          // Tell the server the cart association held. Best effort on purpose:
          // the shopper's design is already safe in the cart by this point, so
          // a failure here is an operator reconciliation problem, not a reason
          // to show them an error.
          if (this.sessionId) {
            fetch(`${this.cfg.proxyPath}/confirm`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: this.sessionId }),
            }).catch(() => {});
          }

          this.session = null;
          this.clearSession();
          this.resetFrame();
          this.setState('complete');
          this.track('completion_shown');
          this.root.querySelector('[data-cp-complete] a')?.focus();
          this.publishCartUpdate();
        } catch (error) {
          this.clearWatchdog();
          this.track('callback_failed');
          this.setState('recoverable_error');
          this.retryBtn?.focus();
        }
      }

      async persistQueueId(queueId) {
        // change.js replaces the whole properties object, so the existing ones
        // have to be read back and resent. Sending only the queue ID here wipes
        // the design token and with it the only durable handle on this line.
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === this.session?.token
        );
        if (!line) throw new Error('cart line went missing');

        const properties = Object.assign({}, line.properties, {
          _colorpark_queue_id: queueId,
          _colorpark_status: 'submitted',
          Artwork: 'Your custom design is saved. Any sample product photo is for illustration only.',
        });

        // The design thumbnail, when the proxy managed to capture one.
        //
        // Written conditionally rather than as a possibly-null value, because
        // Shopify stores a null property as the literal string "null" and the
        // cart would then try to load that as an image. Absent means absent.
        //
        // This is NOT part of verifyCartLine's success test, on purpose. A
        // missing thumbnail is a cosmetic loss; failing verification over one
        // would strand a design the printer is already holding.
        if (this.pendingDesignImage) {
          properties._colorpark_design_image = this.pendingDesignImage;
          // Private properties are hidden by Shopify checkout and order status.
          // Carry a customer-visible reference as well as the production field.
          properties['Your design'] = this.pendingDesignImage;
        }

        const res = await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
        });
        if (!res.ok) throw new Error('could not write the queue ID to the cart');
      }

      async verifyCartLine(queueId) {
        // Line keys change when properties change, so the design token, not the
        // key captured at add time, is what identifies the line afterwards.
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === this.session?.token
        );
        if (!line) return null;

        const p = line.properties || {};
        const ok =
          p._colorpark_queue_id === queueId &&
          p._colorpark_status === 'submitted' &&
          p._colorpark_goods_id === this.cfg.goodsId &&
          p._colorpark_machine_id === this.cfg.machineId;
        return ok ? line : null;
      }

      async fetchCart() {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('could not read the cart');
        return res.json();
      }

      onRetry() {
        this.track('retry_started');
        if (!this.pendingQueueId) {
          this.setState('editing');
          return;
        }
        this.finish(this.pendingQueueId, this.pendingDesignImage).then(() => {
          if (this.state === 'complete') this.track('retry_succeeded');
        });
      }

      startWatchdog() {
        this.clearWatchdog();
        // A spinner that never resolves is worse than an error, because the
        // shopper cannot tell whether their design was kept.
        this.watchdog = window.setTimeout(() => {
          if (this.state !== 'saving') return;
          this.track('callback_failed');
          this.setState('recoverable_error');
          this.retryBtn?.focus();
        }, this.cfg.saveTimeout);
      }

      clearWatchdog() {
        if (this.watchdog) window.clearTimeout(this.watchdog);
        this.watchdog = null;
      }

      /* --------------------------------------------------------------- abandon */

      onKeydown(event) {
        if (event.key === 'Tab' && ['loading', 'adding', 'load_error'].includes(this.state)) {
          const buttons = [...this.root.querySelectorAll('button:not(:disabled)')]
            .filter((button) => button.getClientRects().length && getComputedStyle(button).visibility !== 'hidden');
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (!first) { event.preventDefault(); return; }
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
        if (event.key !== 'Escape') return;
        if (['editing', 'loading', 'load_error'].includes(this.state)) this.onClose();
      }

      async onClose() {
        if (['saving', 'adding'].includes(this.state) || this.closePending) return; // never interrupt a cart write

        if (this.state === 'loading' || this.state === 'load_error') {
          this.invalidateWarm();
          this.discardFrame();
          this.setBusy(false);
          this.setState('idle');
          this.restoreFocus();
          return;
        }

        if (this.state === 'complete' || this.state === 'recoverable_error') {
          this.setState('idle');
          this.restoreFocus();
          return;
        }

        if (!window.confirm(this.dataset.abandonConfirm)) return;

        this.closePending = true;
        try {
          this.track('editor_closed_abandoned');
          const session = this.session;
          this.session = null;
          this.clearSession();
          this.setState('abandoned');

          if (session?.lineKey) {
            try {
              if (this.cfg.abandon === 'remove') {
                await this.removeLine(session);
              } else {
                await this.markIncomplete(session);
              }
            } catch (error) {
              // Removal failed, so fall back to marking it. Either way the line
              // must not be able to reach checkout as a normal undesigned case.
              try {
                await this.markIncomplete(session);
              } catch (ignored) {
                /* the checkout validation function is the backstop */
              }
            }
            this.publishCartUpdate();
          }

          this.resetFrame();
          this.setState('idle');
          this.restoreFocus();
        } finally {
          this.closePending = false;
        }
      }

      async removeLine(session) {
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === session.token
        );
        if (!line) return;

        await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }

      async markIncomplete(session) {
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === session.token
        );
        if (!line) return;

        const properties = Object.assign({}, line.properties, {
          _colorpark_status: 'abandoned',
        });
        await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
        });
      }

      restoreFocus() {
        // The mobile product action forwards clicks to a hidden desktop
        // button. Focus must return to a visible action, never that hidden one.
        const candidates = [this.lastFocused, this.button, ...document.querySelectorAll('[data-ka-customise]')];
        const target = candidates.find((element) => element?.isConnected && element !== document.body &&
          !this.root.contains(element) && element.getClientRects().length &&
          getComputedStyle(element).visibility !== 'hidden');
        target?.focus();
      }

      resetFrame() {
        this.invalidateWarm();
        this.discardFrame();
        this.pendingQueueId = null;
        this.pendingDesignImage = null;
        // A new design is a new session and a new token. Carrying either over
        // would let a second design claim the first one's printer job.
        this.pendingToken = null;
        this.sessionId = null;
        this.syncLabel();
        this.scheduleWarm();
      }

      /* -------------------------------------------------------------- variants */

      onVariantChange(variant) {
        // The variant is read live rather than captured at render time, so the
        // line that gets created is always the one the shopper is looking at.
        if (!variant) {
          this.variantAvailable = false;
          this.button.setAttribute('aria-disabled', 'true');
          return;
        }
        this.variantId = String(variant.id);
        this.variantAvailable = Boolean(variant.available);
        this.button.setAttribute('aria-disabled', this.variantAvailable ? 'false' : 'true');
        this.syncLabel();
      }

      /* --------------------------------------------------------------- session */

      readStoredSession() {
        try {
          return JSON.parse(sessionStorage.getItem(`colorpark:${this.cfg.goodsId}`) || 'null');
        } catch (error) {
          return null;
        }
      }

      persistSession(record = this.session) {
        // Recovery aid only. The cart line properties remain authoritative,
        // and the server record will be once the app proxy exists.
        try {
          sessionStorage.setItem(`colorpark:${this.cfg.goodsId}`, JSON.stringify(record));
          return true;
        } catch (error) {
          return false;
        }
      }

      clearSession() {
        this.pendingWrite = null;
        try {
          sessionStorage.removeItem(`colorpark:${this.cfg.goodsId}`);
        } catch (error) {
          /* private mode */
        }
      }

      /* --------------------------------------------------------------- helpers */

      setBusy(busy) {
        this.button.setAttribute('aria-busy', String(busy));
        this.querySelector('[data-cp-label]').classList.toggle('hidden', busy);
        this.querySelector('[data-cp-loading]').classList.toggle('hidden', !busy);
      }

      clearError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      publishCartUpdate() {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'colorpark-customise',
            productVariantId: this.variantId,
          });
        }
      }

      track(event) {
        // Identifiers and timings only. Never artwork, never a cart token.
        const detail = {
          event: `colorpark_${event}`,
          goods_id: this.cfg.goodsId,
          machine_id: this.cfg.machineId,
          variant_id: this.variantId,
          design_token: this.session?.token || null,
          elapsed_ms: Math.round(performance.now()),
        };
        document.dispatchEvent(new CustomEvent('colorpark:track', { detail }));
        if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      }
    }
  );
}
